import { CoreApiClient } from '../../core/client'
import type { PageResult } from '../../core/types'

export type MemberStatus = 'ACTIVE' | 'SUSPENDED'
export type PointRuleType = 'FIXED' | 'AMOUNT_MULTIPLIER' | 'QUANTITY_MULTIPLIER'
export type PointChangeType = 'EARN' | 'SPEND' | 'ADJUST'

export interface Member {
  id: number
  tenantId: number
  tenantCode?: string | null
  tenantName?: string | null
  moduleCode: string
  moduleName?: string | null
  username: string
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  levelCode: string
  levelName: string
  pointsBalance: number
  growthValue: number
  status: MemberStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface MemberLevel {
  id: number
  tenantId: number
  levelCode: string
  levelName: string
  minGrowth: number
  discountRate: number
  benefits: string
  enabled: boolean
  sortOrder: number
  updatedAt: string
}

export interface PointRule {
  id: number
  tenantId: number
  moduleCode: string
  ruleCode: string
  ruleName: string
  eventCode: string
  ruleType: PointRuleType
  pointsValue: number
  growthValue: number
  maxPoints: number
  enabled: boolean
  ruleJson: string
  remark: string
  updatedAt: string
}

export interface PointTransaction {
  id: number
  tenantId: number
  memberId: number
  moduleCode: string
  eventCode: string
  changeType: PointChangeType
  points: number
  growthValue: number
  balanceAfter: number
  bizNo: string
  remark: string
  createdAt: string
}

export interface CreateMemberRequest {
  tenantId?: number | null
  moduleCode: string
  username: string
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  source?: string | null
}

export interface UpdateMemberRequest {
  nickname: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  source?: string | null
}

export interface UpdateMemberStatusRequest {
  status: MemberStatus
}

export interface UpsertMemberLevelRequest {
  tenantId?: number | null
  levelCode: string
  levelName: string
  minGrowth: number
  discountRate: number
  benefits: string
  enabled: boolean
  sortOrder: number
}

export interface UpsertPointRuleRequest {
  tenantId?: number | null
  moduleCode: string
  ruleCode: string
  ruleName: string
  eventCode: string
  ruleType: PointRuleType
  pointsValue: number
  growthValue: number
  maxPoints: number
  enabled: boolean
  ruleJson?: string | null
  remark: string
}

export interface PointRulePreviewRequest {
  ruleType: PointRuleType
  pointsValue: number
  growthValue: number
  maxPoints: number
  amount?: number | null
  quantity?: number | null
}

export interface PointRulePreview {
  points: number
  growthValue: number
}

export interface MemberAccount {
  id: number
  tenantId: number
  productCode: string
  clientUserId?: number | null
  username: string
  displayName: string
  mobile?: string | null
  email?: string | null
  externalId?: string | null
  levelCode: string
  levelName: string
  growthValue: number
  status: MemberStatus
  source: string
  createdAt: string
  updatedAt: string
}

export interface MemberSubjectBinding {
  id: number
  tenantId: number
  productCode: string
  appCode: string
  bizModuleCode: string
  memberId: number
  clientUserId?: number | null
  bizUserType: string
  bizUserId: string
  bizUsername: string
  bindStatus: string
  bindSource: string
  extraJson?: string | null
  createdAt: string
  updatedAt: string
}

export interface PointAccount {
  id: number
  tenantId: number
  productCode: string
  memberId: number
  balance: number
  frozenBalance: number
  totalEarned: number
  totalSpent: number
  totalExpired: number
  version: number
  createdAt: string
  updatedAt: string
}

export interface PointLedger {
  id: number
  tenantId: number
  productCode: string
  appCode: string
  bizModuleCode: string
  sceneCode: string
  memberId: number
  eventId: string
  bizNo: string
  changeType: string
  points: number
  growthValue: number
  balanceBefore: number
  balanceAfter: number
  expireAt?: string | null
  remark?: string | null
  createdAt: string
}

export interface WalletAccount {
  id: number
  tenantId: number
  productCode: string
  memberId: number
  currency: string
  balanceCent: number
  frozenCent: number
  totalRechargeCent: number
  totalConsumeCent: number
  totalRefundCent: number
  version: number
  status: string
  createdAt: string
  updatedAt: string
}

export interface WalletLedger {
  id: number
  tenantId: number
  productCode: string
  appCode: string
  bizModuleCode: string
  sceneCode: string
  memberId: number
  walletAccountId: number
  currency: string
  eventId: string
  bizNo: string
  tradeType: string
  amountCent: number
  balanceBeforeCent: number
  balanceAfterCent: number
  frozenBeforeCent: number
  frozenAfterCent: number
  relatedLedgerId?: number | null
  remark?: string | null
  createdAt: string
}

export interface BenefitAccount {
  id: number
  tenantId: number
  productCode: string
  memberId: number
  benefitCode: string
  benefitName: string
  totalQuantity: number
  usedQuantity: number
  frozenQuantity: number
  validFrom?: string | null
  validTo?: string | null
  status: string
  extraJson?: string | null
  createdAt: string
  updatedAt: string
}

export interface BenefitLedger {
  id: number
  tenantId: number
  productCode: string
  appCode: string
  bizModuleCode: string
  sceneCode: string
  memberId: number
  benefitCode: string
  eventId: string
  bizNo: string
  changeType: string
  quantity: number
  balanceBefore: number
  balanceAfter: number
  remark?: string | null
  createdAt: string
}

export interface MemberEventLog {
  id: number
  tenantId: number
  productCode: string
  appCode: string
  bizModuleCode: string
  sceneCode: string
  eventId: string
  eventCode: string
  bizNo: string
  memberId?: number | null
  clientUserId?: number | null
  payloadJson?: string | null
  processStatus: string
  errorCode?: string | null
  errorMessage?: string | null
  createdAt: string
  processedAt?: string | null
}

export interface MemberCapabilityRule {
  id: number
  tenantId: number
  productCode: string
  bizModuleCode: string
  sceneCode: string
  ruleCode: string
  ruleName: string
  eventCode: string
  conditionJson: string
  actionJson: string
  priority: number
  enabled: boolean
  updatedAt: string
}

export interface MemberAccountQuery {
  tenantId?: number | null
  productCode?: string
  memberId?: number | null
  clientUserId?: number | null
  username?: string
  keyword?: string
  levelCode?: string
  status?: MemberStatus | ''
  pageNo?: number
  pageSize?: number
}

export interface MemberSubjectBindingQuery {
  tenantId?: number | null
  productCode?: string
  appCode?: string
  bizModuleCode?: string
  memberId?: number | null
  clientUserId?: number | null
  bizUserType?: string
  bizUserId?: string
  bindStatus?: string
  pageNo?: number
  pageSize?: number
}

export interface PointAccountQuery {
  tenantId?: number | null
  productCode?: string
  memberId?: number | null
  pageNo?: number
  pageSize?: number
}

export interface PointLedgerQuery {
  tenantId?: number | null
  productCode?: string
  appCode?: string
  bizModuleCode?: string
  sceneCode?: string
  memberId?: number | null
  eventId?: string
  bizNo?: string
  changeType?: string
  pageNo?: number
  pageSize?: number
}

export interface WalletAccountQuery {
  tenantId?: number | null
  productCode?: string
  memberId?: number | null
  currency?: string
  status?: string
  pageNo?: number
  pageSize?: number
}

export interface WalletLedgerQuery {
  tenantId?: number | null
  productCode?: string
  appCode?: string
  bizModuleCode?: string
  sceneCode?: string
  memberId?: number | null
  eventId?: string
  bizNo?: string
  tradeType?: string
  pageNo?: number
  pageSize?: number
}

export interface BenefitQuery {
  tenantId?: number | null
  productCode?: string
  memberId?: number | null
  benefitCode?: string
  status?: string
  pageNo?: number
  pageSize?: number
}

export interface BenefitLedgerQuery {
  tenantId?: number | null
  productCode?: string
  appCode?: string
  bizModuleCode?: string
  sceneCode?: string
  memberId?: number | null
  benefitCode?: string
  eventId?: string
  bizNo?: string
  changeType?: string
  pageNo?: number
  pageSize?: number
}

export interface MemberEventQuery {
  tenantId?: number | null
  productCode?: string
  appCode?: string
  bizModuleCode?: string
  sceneCode?: string
  eventId?: string
  eventCode?: string
  bizNo?: string
  memberId?: number | null
  clientUserId?: number | null
  processStatus?: string
  pageNo?: number
  pageSize?: number
}

export interface MemberCapabilityRuleQuery {
  tenantId?: number | null
  productCode?: string
  bizModuleCode?: string
  sceneCode?: string
  ruleCode?: string
  eventCode?: string
  enabled?: boolean | null
  keyword?: string
  pageNo?: number
  pageSize?: number
}

export interface UpsertMemberCapabilityRuleRequest {
  tenantId?: number | null
  productCode: string
  bizModuleCode: string
  sceneCode: string
  ruleCode: string
  ruleName: string
  eventCode: string
  conditionJson: string
  actionJson: string
  priority?: number | null
  enabled?: boolean | null
}

export interface UpdateMemberCapabilityRuleStatusRequest {
  tenantId?: number | null
  productCode: string
  ruleCode: string
  enabled: boolean
}

export interface ApplyPointEventRequest {
  tenantId?: number | null
  memberId?: number | null
  moduleCode: string
  eventCode: string
  amount?: number | null
  quantity?: number | null
  bizNo?: string | null
  remark?: string | null
}

export interface AdjustPointsRequest {
  tenantId?: number | null
  memberId?: number | null
  moduleCode: string
  points: number
  growthValue: number
  bizNo?: string | null
  remark?: string | null
}

export class MemberApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  queryMemberCapabilityMembers(query: MemberAccountQuery = {}) {
    return this.client.post<PageResult<MemberAccount>>('/api/admin/member-capability/members/query', query)
  }

  queryMemberCapabilityBindings(query: MemberSubjectBindingQuery = {}) {
    return this.client.post<PageResult<MemberSubjectBinding>>('/api/admin/member-capability/bindings/query', query)
  }

  queryMemberCapabilityPointAccounts(query: PointAccountQuery = {}) {
    return this.client.post<PageResult<PointAccount>>('/api/admin/member-capability/points/accounts/query', query)
  }

  queryMemberCapabilityPointLedgers(query: PointLedgerQuery = {}) {
    return this.client.post<PageResult<PointLedger>>('/api/admin/member-capability/points/ledgers/query', query)
  }

  queryMemberCapabilityWalletAccounts(query: WalletAccountQuery = {}) {
    return this.client.post<PageResult<WalletAccount>>('/api/admin/member-capability/wallet/accounts/query', query)
  }

  queryMemberCapabilityWalletLedgers(query: WalletLedgerQuery = {}) {
    return this.client.post<PageResult<WalletLedger>>('/api/admin/member-capability/wallet/ledgers/query', query)
  }

  queryMemberCapabilityBenefits(query: BenefitQuery = {}) {
    return this.client.post<PageResult<BenefitAccount>>('/api/admin/member-capability/benefits/query', query)
  }

  queryMemberCapabilityBenefitLedgers(query: BenefitLedgerQuery = {}) {
    return this.client.post<PageResult<BenefitLedger>>('/api/admin/member-capability/benefits/ledgers/query', query)
  }

  queryMemberCapabilityEvents(query: MemberEventQuery = {}) {
    return this.client.post<PageResult<MemberEventLog>>('/api/admin/member-capability/events/query', query)
  }

  queryMemberCapabilityRules(query: MemberCapabilityRuleQuery = {}) {
    return this.client.post<PageResult<MemberCapabilityRule>>('/api/admin/member-capability/rules/query', query)
  }

  upsertMemberCapabilityRule(body: UpsertMemberCapabilityRuleRequest) {
    return this.client.post<MemberCapabilityRule>('/api/admin/member-capability/rules/upsert', body)
  }

  updateMemberCapabilityRuleStatus(body: UpdateMemberCapabilityRuleStatusRequest) {
    return this.client.post<MemberCapabilityRule>('/api/admin/member-capability/rules/status', body)
  }
}
